import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const auth = getAuth();
    setCurrentUser(auth.currentUser);
    
    if (!sessionId) {
      setStatus('error');
      setError('No payment session ID found. Please contact support if you believe this is an error.');
      return;
    }
    
    const verifyPayment = async () => {
      try {
        // Call the backend to verify the payment
        const response = await fetch(`/api/verify-payment/${sessionId}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Payment verification failed');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setStatus('success');
          setPaymentDetails(data.data);
          
          // Get the latest user data to show updated credits
          if (auth.currentUser) {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists()) {
              setCurrentUser({
                ...auth.currentUser,
                ...userDoc.data()
              });
            }
          }
        } else {
          throw new Error(data.error || 'Payment was not successful');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
        setError(error.message || 'Failed to verify payment');
      }
    };
    
    verifyPayment();
  }, [sessionId]);
  
  const renderContent = () => {
    switch(status) {
      case 'loading':
        return (
          <>
            <FontAwesomeIcon icon={faSpinner} className="text-purple-500 text-5xl mb-4 animate-spin" />
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Verifying Payment...</h2>
            <p className="text-gray-600 mb-6">
              Please wait while we verify your payment and update your account.
            </p>
          </>
        );
      case 'success':
        return (
          <>
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-5xl mb-4" />
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your payment was processed successfully. Your account has been upgraded to the {paymentDetails?.planId || 'new'} plan 
              with {paymentDetails?.credits || 'additional'} credits.
            </p>
            {currentUser && (
              <p className="text-gray-700 mb-6 font-semibold">
                Your current credit balance: {currentUser.credits || 0}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard/pneumonia')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-colors duration-300"
              >
                Start Using Credits
              </button>
              <button
                onClick={() => navigate('/dashboard/history')}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-300"
              >
                View Payment History
              </button>
            </div>
          </>
        );
      case 'error':
        return (
          <>
            <FontAwesomeIcon icon={faTimes} className="text-red-500 text-5xl mb-4" />
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Payment Verification Failed</h2>
            <p className="text-red-600 mb-6">
              {error || 'There was an issue verifying your payment. Please contact support.'}
            </p>
            <p className="text-gray-600 mb-6">
              If you believe your payment was successful, please contact support with your payment reference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-colors duration-300"
              >
                Return to Dashboard
              </button>
              <button
                onClick={() => navigate('/plans')}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-300"
              >
                View Plans
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 font-['Noto Sans']">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200 max-w-2xl w-full mx-4">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentSuccess;